/**
 * useVoiceTranscribe — record microphone audio and POST to /api/whisper/transcribe.
 *
 * Two modes:
 *  • One-shot (default): start → user speaks → stop → single Whisper call.
 *  • Continuous: auto-chunks every CHUNK_MS into independent WebM blobs that
 *    each get transcribed in parallel, with text appended in arrival order so
 *    the user sees their words flow into the composer "live-ish".
 *
 * Whisper has no native streaming endpoint, so continuous mode trades a tiny
 * bit of latency (one chunk worth) for the appearance of live dictation.
 */

import { useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_MS = 6000;  // 6-second segments balance latency vs. Whisper quality

export default function useVoiceTranscribe({ onText } = {}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [continuous, setContinuous] = useState(false);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const cycleTimerRef = useRef(null);
  const continuousRef = useRef(false);  // mirrors state for closure-safe reads
  const inflightRef = useRef(0);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const pickMime = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
    return '';
  };

  // Upload a single completed blob to Whisper. Tracks an inflight counter
  // so the UI can show "transcribing" until the last chunk finishes.
  const transcribeBlob = async (blob, ext) => {
    if (!blob || !blob.size) return;
    inflightRef.current += 1;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, `voice.${ext}`);
      const r = await apiFetch(`${API_URL}/api/whisper/transcribe`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Transcription failed');
      }
      const data = await r.json();
      if (data?.text) onText?.(data.text);
    } catch (e) {
      setError(e?.message || 'Transcription failed');
      toast.error(e?.message || 'Voice transcription failed');
    } finally {
      inflightRef.current = Math.max(0, inflightRef.current - 1);
      if (inflightRef.current === 0) setBusy(false);
    }
  };

  // Build a fresh recorder bound to the existing mic stream. Used by both
  // one-shot start AND continuous-mode chunk cycling.
  const buildRecorder = (mime) => {
    const stream = streamRef.current;
    if (!stream) return null;
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      const collected = chunksRef.current;
      chunksRef.current = [];
      const blobMime = rec.mimeType || 'audio/webm';
      const ext = blobMime.includes('mp4') ? 'm4a' : 'webm';
      const blob = new Blob(collected, { type: blobMime });

      // If we're in continuous mode AND the user hasn't stopped recording,
      // immediately spin up a new recorder for the next chunk BEFORE we
      // upload — minimises the audio gap between chunks.
      if (continuousRef.current) {
        const next = buildRecorder(mime);
        recorderRef.current = next;
        if (next) {
          try { next.start(); } catch { /* may race with cleanup; safe to ignore */ }
        }
      } else {
        // One-shot: this was the final chunk → cleanup the mic stream.
        cleanupStream();
      }

      // Fire-and-forget upload; UI tracks busy via inflight counter.
      transcribeBlob(blob, ext);
    };
    return rec;
  };

  const start = useCallback(async ({ continuous: cont = false } = {}) => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      const msg = 'Voice input isn\'t supported in this browser.';
      setError(msg);
      toast.error(msg);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = buildRecorder(mime);
      if (!rec) return;
      recorderRef.current = rec;
      continuousRef.current = !!cont;
      setContinuous(!!cont);
      rec.start();
      setRecording(true);

      // Continuous mode: every CHUNK_MS, stop the active recorder (which fires
      // onstop → builds a fresh one + sends the completed blob to Whisper).
      if (cont) {
        cycleTimerRef.current = setInterval(() => {
          const r = recorderRef.current;
          if (r && r.state === 'recording') {
            try { r.stop(); } catch { /* race-safe */ }
          }
        }, CHUNK_MS);
      }
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Microphone permission denied.'
        : 'Could not access your microphone.';
      setError(msg);
      toast.error(msg);
      cleanupStream();
    }
  }, []);

  const stop = useCallback(() => {
    // Order matters: clear timer first so the next cycle doesn't fire, then
    // flip continuousRef so onstop knows NOT to spin up a new recorder.
    if (cycleTimerRef.current) {
      clearInterval(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
    continuousRef.current = false;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* race-safe */ }
    }
    recorderRef.current = null;
    setRecording(false);
    setContinuous(false);
  }, []);

  return { recording, busy, continuous, start, stop, error };
}
